import { Component, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController, NavParams } from '@ionic/angular';

import { extractTicketId } from 'src/app/utils/custom-fields.js';
import { environment } from 'src/environments/environment';
import {
  AgentTopicTools,
  Automation,
  Customer,
  CustomField,
  GlobalCustomField,
  POSTING_DESTINATION_MODES,
  PostingDestinationMode,
  SimpleConditions,
  SimpleConditionsLeaf,
  SlackChannel,
  TICKET_TYPE_VISIBILITY,
  TicketTypeVisibility,
} from 'src/models';
import { AIAutofillService } from 'src/services/ai-autofill.service.js';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';
import { TicketTypesService } from 'src/services/ticket-types.service';

const POSTING_DESTINATION_MODE_OPTIONS = [
  { value: POSTING_DESTINATION_MODES.ALWAYS_DM, label: 'Post to DM with Unthread Bot' },
  { value: POSTING_DESTINATION_MODES.DEFAULT_CHANNEL, label: 'Follow channel settings to determine where to post' },
  {
    value: POSTING_DESTINATION_MODES.TICKET_TYPE_SPECIFIC_CHANNEL,
    label: 'Post to Slack channel',
  },
] as const;
@Component({
  selector: 'app-edit-ticket-type',
  templateUrl: './edit-ticket-type.component.html',
  styleUrls: ['./edit-ticket-type.component.scss'],
})
export class EditTicketTypeComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput;

  // Expose the enum/const to the template
  public POSTING_DESTINATION_MODES = POSTING_DESTINATION_MODES;

  isDev: boolean;

  isLoading = false;
  name: string;
  description: string;
  visibility: TicketTypeVisibility = TICKET_TYPE_VISIBILITY.SUBMITTABLE;
  id?: string;
  prioritySelector?: number;
  titleInput?: number;
  descriptionInput?: number;
  fields: any[];
  defaultCustomerId?: string;
  postingDestinationMode: PostingDestinationMode = POSTING_DESTINATION_MODES.DEFAULT_CHANNEL;
  defaultSlackChannelId?: string;
  defaultCustomer: any;
  defaultSlackChannel: SlackChannel;
  projectId: string;
  tools?: AgentTopicTools[];
  instructions?: string;
  postingDestinationChannelIds: string[] = [];
  postingDestinationChannels: SlackChannel[] = [];
  isSuggestingInstruction: boolean = false;
  showTopicInstructionField: boolean;
  isTicketSpecificPostingEnabled = false;
  userFormNote?: string | null;
  pagerDutyServiceId?: string;
  pagerDutyService?: { id: string; name: string };
  postingDestinationModes = POSTING_DESTINATION_MODE_OPTIONS;

  constructor(
    navParams: NavParams,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private ticketTypesService: TicketTypesService,
    private searchablePopoverService: SearchablePopoverService,
    private customersService: CustomersService,
    private slackService: SlackService,
    private aiAutofillService: AIAutofillService,
    private projectsService: ProjectsService,
  ) {
    this.id = navParams.get('id');
    this.projectId = navParams.get('projectId');
    this.isDev = environment.environment === 'dev';
    this.showTopicInstructionField = authService.tenant.useTopicAwareAgent;
  }

  ngOnInit(): void {
    this.isTicketSpecificPostingEnabled = this.authService.isFeatureEnabled('TICKET_SPECIFIC_POSTING_DESTINATION');

    if (this.id) {
      this.loadTicketType();
    } else {
      this.fields = [];
      if (!this.isTicketSpecificPostingEnabled) {
        this.postingDestinationMode = POSTING_DESTINATION_MODES.DEFAULT_CHANNEL;
      }
      setTimeout(() => this.nameInput.setFocus(), 500);
    }
  }

  arrayTrackBy(index, item) {
    return index;
  }

  addNewTool() {
    this.searchablePopoverService.automations({
      event,
      projectId: this.projectId,
      callback: (id: string, _name: string, data: Automation) => {
        const tool: any = { topicId: this.id, description: '' };
        tool.automationId = id;
        tool.automation = data;
        this.tools = (this.tools ?? []).concat(tool);
      },
    });
  }

  selectAutomation(tool) {
    this.searchablePopoverService.automations({
      event,
      selectedAutomationId: tool.automationId,
      projectId: this.projectId,
      callback: (id: string, _name: string, data: Automation) => {
        tool.automationId = id;
        tool.automation = data;
      },
    });
  }

  removeAutomation(tool) {
    this.tools = this.tools.filter((t) => t !== tool);
  }

  selectEntity(tool) {
    this.searchablePopoverService.entities({
      event,
      selectedEntityId: tool?.approverId,
      type: tool?.approverType,
      callback(id, _name, user) {
        tool.approverId = id;
        tool.approverType = user.type;
        tool.approver = user;
      },
    });
  }

  removeEntity(tool) {
    tool.approverId = undefined;
    tool.approverType = undefined;
    tool.approver = undefined;
  }

  validateSimpleConditional(
    conditonal: SimpleConditions,
    validityCheck: (leaf: SimpleConditionsLeaf) => boolean,
  ): boolean {
    const recurse = (simpleSchema: SimpleConditions | SimpleConditions[]) => {
      if (Array.isArray(simpleSchema)) {
        // if the schema is an array, this format is unsupported: return false
        return false;
      }

      if (!Object.keys(simpleSchema).length) {
        // empty schema
        return true;
      }

      if ('operator' in simpleSchema && simpleSchema.operator) {
        // this is a leaf
        if (!validityCheck(simpleSchema)) {
          return false; // Return false to indicate this should be removed
        }
        return true; // Valid leaf
      }

      // Handle 'or' conditions
      if ('or' in simpleSchema && Array.isArray(simpleSchema.or)) {
        // Check each subcondition and return false immediately if any is invalid
        for (const subCondition of simpleSchema.or) {
          if (!recurse(subCondition)) {
            return false; // Return false immediately when an invalid condition is found
          }
        }
      }

      // Handle 'and' conditions
      if ('and' in simpleSchema && Array.isArray(simpleSchema.and)) {
        // Check each subcondition and return false immediately if any is invalid
        for (const subCondition of simpleSchema.and) {
          if (!recurse(subCondition)) {
            return false; // Return false immediately when an invalid condition is found
          }
        }
      }

      return true;
    };

    return recurse(conditonal);
  }

  removeInvalidConditionalsOnCustomFields(
    fields: CustomField[],
    globalFields: GlobalCustomField[] = [],
  ): CustomField[] {
    // state
    // keep track of field ids
    const seenFieldIds = new Set<string>(globalFields.map((f) => f.id));
    const res: CustomField[] = [];

    // for each custom field
    for (const field of fields) {
      if (field.conditions && 'simple' in field.conditions && field.conditions.simple) {
        // if the field has a simple conditional, check for validity
        field.conditions['simple'] = this.validateSimpleConditional(
          field.conditions.simple,
          (conditions: SimpleConditionsLeaf) => {
            if (conditions.leftValueFrom) {
              const leftValueReference = extractTicketId(conditions.leftValueFrom);
              return leftValueReference && seenFieldIds.has(leftValueReference);
            }

            if (conditions.rightValueFrom) {
              const rightValueReference = extractTicketId(conditions.rightValueFrom);
              return rightValueReference && seenFieldIds.has(rightValueReference);
            }

            return true;
          },
        )
          ? field.conditions.simple
          : {};
      }
      // once we're doing, add id to seen and update res
      seenFieldIds.add(field.id);
      res.push(field);
    }

    return res;
  }

  async loadTicketType() {
    try {
      const project = await this.projectsService.getById(this.projectId);
      const ticketType = await this.ticketTypesService.getById(this.id);

      this.name = ticketType.name;
      this.description = ticketType.description;
      this.prioritySelector = ticketType.prioritySelector;
      this.titleInput = ticketType.titleInput;
      this.descriptionInput = ticketType.descriptionInput;
      this.visibility = ticketType.visibility;
      this.fields = ticketType.fields
        ? this.removeInvalidConditionalsOnCustomFields(ticketType.fields, project.fields)
        : [];
      this.defaultCustomerId = ticketType.defaultCustomerId;
      this.defaultSlackChannelId = ticketType.defaultSlackChannelId;
      this.projectId = ticketType.projectId;
      this.instructions = ticketType.instructions;
      this.tools = ticketType.tools;
      this.postingDestinationMode = ticketType.postingDestinationMode ?? POSTING_DESTINATION_MODES.DEFAULT_CHANNEL;
      this.postingDestinationChannelIds = ticketType.postingDestinationChannelIds ?? [];
      this.userFormNote = ticketType.userFormNote;
      this.pagerDutyServiceId = ticketType.pagerDutyServiceId;
      this.pagerDutyService = ticketType.pagerDutyService;

      // Fetch full SlackChannel objects if IDs exist and feature is enabled
      if (this.isTicketSpecificPostingEnabled && this.postingDestinationChannelIds.length > 0) {
        try {
          this.postingDestinationChannels = await Promise.all(
            this.postingDestinationChannelIds.map((id) => this.slackService.getChannel(id)),
          );
          // Filter out any channels that couldn't be fetched (e.g., deleted or inaccessible)
          this.postingDestinationChannels = this.postingDestinationChannels.filter((ch) => ch != null);
          // Ensure postingDestinationChannelIds is consistent with fetched channels
          this.postingDestinationChannelIds = this.postingDestinationChannels.map((ch) => ch.id);
        } catch (fetchError) {
          console.error('Error fetching posting destination channels:', fetchError);
          // Handle error appropriately, maybe clear the selections
          this.postingDestinationChannels = [];
          this.postingDestinationChannelIds = [];
          this.msgSrvc.showError('Could not load some selected channels.');
        }
      } else {
        this.postingDestinationChannels = [];
        // Ensure IDs are also cleared if the feature is disabled,
        // even if they existed in the DB, to prevent inconsistent state if flag is toggled.
        if (!this.isTicketSpecificPostingEnabled) {
          this.postingDestinationChannelIds = [];
        }
      }

      if (this.defaultCustomerId) {
        this.defaultCustomer = await this.customersService.get(this.defaultCustomerId);
      }

      if (this.defaultSlackChannelId) {
        this.defaultSlackChannel = await this.slackService.getChannel(this.defaultSlackChannelId);
      }
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  async submit() {
    if (!this.name?.trim()) {
      await this.msgSrvc.show('Please enter a name for the ticket type.');
      this.nameInput.setFocus();

      return;
    }

    if (
      this.isTicketSpecificPostingEnabled &&
      this.postingDestinationMode === POSTING_DESTINATION_MODES.TICKET_TYPE_SPECIFIC_CHANNEL &&
      (!this.postingDestinationChannelIds || this.postingDestinationChannelIds.length === 0)
    ) {
      await this.msgSrvc.show('Please select at least one allowed channel when routing based on ticket type.');
      return;
    }

    this.isLoading = true;

    const fields = this.fields.map((field, index) => ({
      ...field,
      label: field.label?.trim() ? field.label : `Field ${index + 1}`,
      options: field.externalSelect != null ? null : field.options,
    }));

    const ticketTypeData = {
      name: this.name,
      description: this.description,
      prioritySelector: this.prioritySelector,
      titleInput: this.titleInput,
      descriptionInput: this.descriptionInput,
      ...(this.isTicketSpecificPostingEnabled && {
        postingDestinationMode: this.postingDestinationMode,
        postingDestinationChannelIds: this.postingDestinationChannelIds,
      }),
      fields,
      defaultCustomerId: this.defaultCustomerId,
      defaultSlackChannelId: this.defaultSlackChannelId,
      projectId: this.projectId,
      instructions: this.instructions,
      tools: this.tools,
      visibility: this.visibility,
      userFormNote: this.userFormNote?.trim() ? this.userFormNote : null,
      pagerDutyServiceId: this.pagerDutyServiceId,
    };

    try {
      if (this.id) {
        await this.ticketTypesService.updatePartial(this.id, ticketTypeData);
        this.msgSrvc.show('Success!');
      } else {
        const newTicketType = await this.ticketTypesService.create(ticketTypeData);
        this.id = newTicketType.id; // Update id after creation
        this.msgSrvc.show('Success!');
      }
      this.modalCtrl.dismiss({ saved: true });
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete ticket type?',
      subHeader: `This can't be undone.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.ticketTypesService.delete(this.id);
              this.isLoading = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async duplicate() {
    const alert = await this.alertCtrl.create({
      header: 'Duplicate ticket type?',
      subHeader: `You can confirm details before you save.`,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Duplicate',
          handler: () => {
            this.id = undefined;
            setTimeout(() => {
              this.name = `Copy of ${this.name}`;
              this.nameInput.setFocus();
            }, 500);
          },
        },
      ],
    });
    alert.present();
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  selectCustomer() {
    this.searchablePopoverService.customers({
      event,
      selectedCustomerId: this.defaultCustomer?.id,
      projectId: this.projectId,
      callback: async (id: string, name: string) => {
        this.defaultCustomerId = id ?? null;
        this.defaultCustomer = id
          ? {
              id,
              name,
            }
          : null;
      },
    });
  }

  unlinkCustomer() {
    this.defaultCustomerId = null;
    this.defaultCustomer = null;
  }

  onSlackChannelChanged(slackChannel?: SlackChannel) {
    this.defaultSlackChannelId = slackChannel?.id ?? null;
    this.defaultSlackChannel = slackChannel ?? null;
  }

  onPostingDestinationChannelsChange(channels: SlackChannel[]) {
    if (this.isTicketSpecificPostingEnabled) {
      this.postingDestinationChannels = channels ?? [];
      this.postingDestinationChannelIds = this.postingDestinationChannels.map((channel) => channel.id);
    }
  }

  async suggestTopicInstruction() {
    // deactivate button and show loading state
    this.isSuggestingInstruction = true;
    // autofill
    this.instructions = await this.aiAutofillService.suggestTopicInstruction({
      projectId: this.projectId,
      name: this.name,
      description: this.description,
    });
    // remove loading state
    this.isSuggestingInstruction = false;
  }

  genericCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  showPagerDutyServiceOptions() {
    this.searchablePopoverService.pagerDutyServices({
      event,
      selectedServiceId: this.pagerDutyServiceId,
      showNoneOption: true,
      callback: (id: string, name: string) => {
        this.pagerDutyServiceId = id ?? null;
        this.pagerDutyService = id ? { id, name } : null;
      },
    });
  }

  async addPostingDestinationChannel() {
    this.searchablePopoverService.customers({
      event,
      projectId: this.projectId,
      type: 'slack',
      callback: (id: string, name: string, customer: Customer) => {
        if (this.isTicketSpecificPostingEnabled && customer.slackChannel) {
          if (!this.postingDestinationChannels) {
            this.postingDestinationChannels = [];
          }

          if (!this.postingDestinationChannelIds) {
            this.postingDestinationChannelIds = [];
          }

          this.postingDestinationChannels.push(customer.slackChannel);
          this.postingDestinationChannelIds.push(customer.slackChannel.id);
        }
      },
    });
  }

  removePostingDestinationChannel(channel: SlackChannel) {
    this.postingDestinationChannels = this.postingDestinationChannels.filter((ch) => ch.id !== channel.id);
    this.postingDestinationChannelIds = this.postingDestinationChannelIds.filter((id) => id !== channel.id);
  }
}
